/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {PaymentType} from '../util/interfaces';
import {ModelResponse} from "@dvrd/dvr-controls";
import Subscription from "./subscriptionModel";

interface Props extends BaseProps {
    stripe_id?: string;
    account_number?: string;
    bank?: string;
    expires?: string;
    payment_type?: PaymentType;
    subscription_id?: string;
    subscription?: Subscription | any;
}

interface Fields extends BaseFields {
    stripe_id: string;
    account_number: string;
    bank: string;
    expires: string;
    payment_type: PaymentType;
    subscription_id: string;
    subscription: Subscription | null;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    stripe_id: '',
    account_number: '',
    bank: '',
    expires: '',
    payment_type: PaymentType.IDEAL,
    subscription_id: '',
    subscription: null,
};

const fieldsDefinition: { [key in keyof Omit<Fields, 'subscription'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    stripe_id: createFieldDef(ModelFieldType.STRING),
    account_number: createFieldDef(ModelFieldType.STRING),
    bank: createFieldDef(ModelFieldType.STRING),
    expires: createFieldDef(ModelFieldType.STRING),
    subscription_id: createFieldDef(ModelFieldType.STRING),
    payment_type: createFieldDef(ModelFieldType.STRING, PaymentType.IDEAL),
};

const ignoreProps: Array<keyof Props> = ['subscription'];

export default class PaymentMethod extends BaseModel<Props, Fields> {
    static get = (id: string, callback: ModelResponse<PaymentMethod>) => {

    };

    static getAll = (callback: ModelResponse<PaymentMethod[]>) => {

    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get stripeId(): string {
        return this.fields.stripe_id;
    }

    set stripeId(stripe_id: string) {
        this.fields.stripe_id = stripe_id;
    }

    get accountNumber(): string {
        return this.fields.account_number;
    }

    set accountNumber(account_number: string) {
        this.fields.account_number = account_number;
    }

    get bank(): string {
        return this.fields.bank;
    }

    set bank(bank: string) {
        this.fields.bank = bank;
    }

    get expires(): string {
        return this.fields.expires;
    }

    set expires(expires: string) {
        this.fields.expires = expires;
    }

    get paymentType(): PaymentType {
        return this.fields.payment_type;
    }

    get displayPaymentType(): string {
        switch (this.paymentType) {
            case PaymentType.CARD:
                return 'Creditcard';
            case PaymentType.IDEAL:
                return 'iDeal';
            case PaymentType.SEPA:
                return 'Sepa Debit';
        }
    }

    set paymentType(payment_type: PaymentType) {
        this.fields.payment_type = payment_type;
    }

    get subscriptionId(): string {
        return this.fields.subscription_id;
    }

    set subscriptionId(subscription_id: string) {
        this.fields.subscription_id = subscription_id;
    }

    get subscription(): Subscription | null {
        return this.fields.subscription;
    }

    set subscription(subscription: Subscription | null) {
        this.fields.subscription = subscription;
        if (subscription) this.fields.subscription_id = subscription.id;
    }

    save = (callback?: ModelResponse<PaymentMethod>) => {

    };

    update = (callback?: ModelResponse<PaymentMethod>) => {

    };

    delete = (callback?: ModelResponse<PaymentMethod>) => {

    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.subscription) {
                this.fields.subscription = props.subscription instanceof Subscription ? props.subscription : new Subscription(props.subscription);
            }
        }
    };
}